/*
* AudioManagementContainer.js (Please do not remove this comment)
* This file is part of the Audio Management System.
* There are three components be included in this file:
* BE FRIENDLY
* 1. AudioDatePicker: A date picker component for selecting a date.
* 2. AudioFileList: A component for displaying a list of audio files that date specified by AudioDatePicker.
* 3. AudioWavSurface: A component for playing audio files. (use wavsurfer.js)
*
* my version:
* @mui/material: 7.1.0
* @mui/x-date-pickers: 8.3.1
* date-fns: 4.1.0
* wavesurfer.js: 7.9.5
*/

import React, { useState, useEffect } from 'react';
import { format, isValid } from 'date-fns'; // Ensure isValid is imported if not already
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import AudioDatePicker from './AudioDatePicker';
import AudioFileList from './AudioFileList';
import AudioWavSurface from './AudioWavSurface';
import { useAuthContext } from '../hooks/useAuthContext';

// 創建並初始化 IndexedDB
const initDB = async () => {
  return new Promise((resolve, reject) => {
    // 打開或創建數據庫，版本為 1
    const request = window.indexedDB.open('AudioCache', 1);
    
    // 如果需要創建/升級數據庫
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // 創建 objectStore，如果不存在
      if (!db.objectStoreNames.contains('audioFiles')) {
        db.createObjectStore('audioFiles');
      }
    };
    
    // 成功打開數據庫
    request.onsuccess = (event) => {
      console.log('IndexedDB initialized successfully');
      resolve(event.target.result);
    };
    
    // 打開數據庫失敗
    request.onerror = (event) => {
      console.error('Error initializing IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });
};

const AudioManagementContainer = ({ clientId }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // const [loading, setLoading] = useState(false); // OLD STATE
  const [isLoadingAudioList, setIsLoadingAudioList] = useState(false); // NEW: For the list of files
  const [isLoadingAudioContent, setIsLoadingAudioContent] = useState(false); // NEW: For the selected file's content
  const [error, setError] = useState(null);
  const [soundLevel, setSoundLevel] = useState(60);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [audioDBKey, setAudioDBKey] = useState(null);
  const [dbInitialized, setDbInitialized] = useState(false);
  const { user } = useAuthContext();

  // 初始化 IndexedDB
  useEffect(() => {
    const setupDB = async () => {
      try {
        await initDB();
        setDbInitialized(true);
      } catch (err) {
        console.error('Failed to initialize IndexedDB:', err);
        setError('Unable to initialize audio cache. Some features may not work properly.');
      }
    };
    
    setupDB();
    
    // 清理時嘗試關閉數據庫連接
    return () => {
      // 關閉數據庫連接的清理邏輯（如果需要）
    };
  }, []);

  // Fetch available dates
  useEffect(() => {
    if (!clientId || !user?.token) return;
    
    const fetchAvailableDates = async () => {
      setLoadingDates(true);
      setError(null);
      
      try {
        const response = await fetch(`/api/v1/device/audio/available-date/${clientId}`, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          setAvailableDates(data);
          console.log('Available dates:', data);
        } else if (data && Array.isArray(data.availableDates)) {
          setAvailableDates(data.availableDates);
          console.log('Available dates:', data.availableDates);
        } else {
          console.warn('Unexpected response format for available dates:', data);
          setAvailableDates([]);
        }
      } catch (err) {
        console.error('Error fetching available dates:', err);
        setError('Unable to fetch available audio dates. Please try again later.');
        setAvailableDates([]);
      } finally {
        setLoadingDates(false);
      }
    };
    
    fetchAvailableDates();
  }, [clientId, user?.token]);

  // Load audio files for the selected date
  useEffect(() => {
    if (!selectedDate) {
      setAudioFiles([]); // Clear files if no date is selected
      setSelectedFile(null); // Reset selected file when date changes
      setAudioUrl(null);     // Also clear related player states
      setAudioDBKey(null);   // Clear player key when date changes
      setIsLoadingAudioList(false); // Ensure loading list is false
      return;
    }
    
    setIsLoadingAudioList(true); // Use isLoadingAudioList
    setError(null);
    setAudioFiles([]);
    setSelectedFile(null); // <<< --- UNCOMMENT THIS LINE
    setAudioUrl(null);
    setAudioDBKey(null); // Clear player key when date changes
    
    const formattedDate = format(selectedDate, 'yyyy-MM-dd');

    const fetchAudioFiles = async () => {
      try {
        // Ensure clientId and user.token are available before fetching
        if (!clientId || !user?.token) {
          throw new Error("Client ID or user token missing.");
        }
        const response = await fetch(`/api/v1/device/audio/${clientId}/${formattedDate}`, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data)) {
          setAudioFiles(data);
        } else if (data && Array.isArray(data.audioFiles)) {
          setAudioFiles(data.audioFiles);
        } else {
          console.warn('Unexpected response format for audio files:', data);
          setAudioFiles([]);
        }
      } catch (err) {
        console.error('Error fetching audio files:', err);
        setError('Unable to fetch audio files. Please try again later.');
        setAudioFiles([]);
      } finally {
        setIsLoadingAudioList(false); // Use isLoadingAudioList
      }
    };
    fetchAudioFiles();
  }, [selectedDate, clientId, user?.token]); // Added clientId and user.token as they are used in fetch

  // Update URL when a new file is selected
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    const fetchAndCacheAudio = async () => {
      // Add isValidDate check for selectedDate as a safeguard
      if (!selectedFile || !selectedDate || (selectedDate && !isValid(selectedDate)) || !dbInitialized) {
        // If selectedFile is null (due to date change) or selectedDate is invalid, abort.
        setIsLoadingAudioContent(false); // Ensure loading state is reset if we abort early
        return;
      }
      
      setIsLoadingAudioContent(true); // Use isLoadingAudioContent
      setError(null);
      
      // If there was an old audioUrl (blob), revoke it.
      // This was previously inside the try block, but better to do it before new fetch.
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null); // Clear the state after revoking
      }
      // No need to setAudioUrl(null) again if it wasn't a blob or already null.
      
      // const formattedDate = format(selectedDate, 'yyyy-MM-dd'); // Line 254
      // Ensure selectedDate is valid before formatting
      const formattedDate = isValid(selectedDate) ? format(selectedDate, 'yyyy-MM-dd') : '';
      if (!formattedDate && selectedDate) { // If selectedDate was not null but invalid
          console.error("Selected date is invalid, cannot format for audio key.");
          setIsLoadingAudioContent(false);
          setError("Invalid date selected.");
          return;
      }
      const audioKey = `${clientId}-${formattedDate}-${selectedFile.name}`;
      
      try {
        // 嘗試從 IndexedDB 獲取音頻
        const db = await initDB();
        const tx = db.transaction('audioFiles', 'readonly'); // 'readonly' if just getting
        const store = tx.objectStore('audioFiles');
        const cachedAudioRequest = store.get(audioKey);

        let audioBlob;

        await new Promise((resolve, reject) => {
          cachedAudioRequest.onsuccess = () => {
            if (cachedAudioRequest.result) {
              audioBlob = cachedAudioRequest.result;
              resolve();
            } else {
              resolve('fetch'); // Needs to be fetched
            }
          };
          cachedAudioRequest.onerror = (event) => reject(event.target.error);
        }).then(async (result) => {
          if (result === 'fetch') {
            const url = `/api/v1/device/audio/${clientId}/${formattedDate}/${selectedFile.name}`;
            const response = await fetch(url, {
              headers: { 'Authorization': `Bearer ${user.token}` },
              signal
            });
            if (signal.aborted) return;
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            audioBlob = await response.blob();
            if (signal.aborted) return;

            // Store in IndexedDB
            const storeTx = db.transaction('audioFiles', 'readwrite');
            const audioStore = storeTx.objectStore('audioFiles');
            audioStore.put(audioBlob, audioKey);
          }
        });
        
        if (signal.aborted) return;

        setAudioDBKey(audioKey);
        const newBlobUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(newBlobUrl); // Set the new blob URL for the player
        setIsPlaying(false); // Reset playing state for the new file
        
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching/caching audio:', err);
          setError('Unable to load audio. Please try again later.');
          setAudioDBKey(null); // Clear key on error
          setAudioUrl(null);   // Clear URL on error
        }
      } finally {
        if (!signal.aborted) {
          setIsLoadingAudioContent(false); // Use isLoadingAudioContent
        }
      }
    };

    if (selectedFile && dbInitialized) {
      fetchAndCacheAudio();
    } else {
      setIsLoadingAudioContent(false);
      setAudioDBKey(null);
      setAudioUrl(null);
    } 

    return () => {
      controller.abort();
      // Clean up the blob URL when the selectedFile changes or component unmounts
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [selectedFile, selectedDate, clientId, user?.token, dbInitialized]); // dbInitialized is important here
  
  // Simulate sound level changes (in a real application, this might come from audio analysis or API)
  useEffect(() => {
    if (!selectedFile || !isPlaying) return;
    
    const interval = setInterval(() => {
      // Random variation between 40-80, simulating sound level changes
      setSoundLevel(40 + Math.random() * 40);
    }, 500);
    
    return () => clearInterval(interval);
  }, [selectedFile, isPlaying]);
  
  // Handle date change
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };
  
  // Handle file selection
  const handleFileSelect = (file) => {
    // 如果已經選擇了同一個文件，不做任何操作
    if (selectedFile && selectedFile.name === file.name) return;

    // 不要清理 audioUrl，讓 AudioWavSurface 自己處理
    // if (audioUrl) {
    //   URL.revokeObjectURL(audioUrl);
    // }

    // 設置新的選擇文件
    setSelectedFile(file);

    // 自動將選中的檔案 scroll 到可見位置（可選）
    // setTimeout(() => {
    //   const el = document.getElementById(`audio-file-item-${file.name}`);
    //   if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    // }, 0);
  };
  
  // Handle playback status change
  const handlePlayingChange = (playing) => {
    setIsPlaying(playing);
  };

  return (
    <Box sx={{ 
      p: 1, // Padding will be inside the 100% height
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', // Changed from calc(100vh - 48px)
      // Ensure the Dialog's content area provides a constrained height for '100%' to work.
      // If the Dialog's content area is also a flex item, AudioManagementContainer might need flexGrow: 1 instead of height: '100%'.
      minHeight: 0, // Still important for nested flex scrolling
    }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}
      
      {/* Top section: DatePicker and WavSurface - should not grow */}
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: 'stretch',
          flexShrink: 0, // Prevent this section from shrinking
          mb: 2,
        }}
      >
        {/* DatePicker */}
        <Box sx={{ flex: { sm: '0 0 200px' } }}>
          {loadingDates ? (
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                height: '100%' // Make loader fill datepicker area
              }}
            >
              <CircularProgress size={40} />
            </Paper>
          ) : (
            <AudioDatePicker 
              availableDates={availableDates}
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
          )}
        </Box>
        
        {/* AudioWavSurface Area */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          {isLoadingAudioContent ? (
            <Paper elevation={3} sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', p: 3 }}>
              <CircularProgress />
              {selectedFile && <Typography sx={{mt:1}}>{`Loading ${selectedFile.name}...`}</Typography>}
            </Paper>
          ) : audioDBKey ? (
            <AudioWavSurface 
              blobUrl={audioUrl}
              dbKey={audioDBKey}
              useIndexedDB={true}
              soundLevel={soundLevel}
              onPlayingChange={handlePlayingChange}
            />
          ) : ( 
            <Paper elevation={3} sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', p: 3 }}>
              <Typography variant="h6" color="text.secondary" align="center" gutterBottom>
                Audio Player
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                {selectedDate 
                  ? 'Select an audio file to play'
                  : loadingDates 
                    ? 'Loading available dates...'
                    : availableDates.length > 0 
                      ? 'Please select a date with available audio'
                      : 'No audio recordings available'}
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
      
      {/* Bottom section: File list area - this should grow */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0, 
        overflow: 'hidden', 
      }}>
        {isLoadingAudioList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          selectedDate && audioFiles.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
              {/* <Typography variant="h6" gutterBottom sx={{ flexShrink: 0, px:1, pt:1 }}>
                Available Audio Files
              </Typography> */}
              <AudioFileList 
                audioFiles={audioFiles}
                onFileSelect={handleFileSelect}
                basePath={selectedDate && isValid(selectedDate) ? `/api/v1/device/audio/${clientId}/${format(selectedDate, 'yyyy-MM-dd')}` : ''}
                selectedFile={selectedFile}
                sx={{ flexGrow: 1, minHeight: 0 }} 
              />
            </Box>
          ) : selectedDate && !isLoadingAudioList ? (
             <Paper /* ... placeholder ... */ sx={{ flexGrow: 1, display: 'flex', /* ... */ }} >
                <Typography color="text.secondary">No audio files found for this date.</Typography>
             </Paper>
          ) : !selectedDate && !loadingDates ? (
             <Paper /* ... placeholder ... */ sx={{ flexGrow: 1, display: 'flex', /* ... */ }} >
                <Typography color="text.secondary">
                  {availableDates.length > 0 
                    ? 'Please select a date to view available audio files'
                    : 'No audio recordings available for this device'}
                </Typography>
             </Paper>
          ) : null
        )}
      </Box>
    </Box>
  );
};

export default AudioManagementContainer;
